import React, { useState, useEffect } from 'react';
import logo from '../../assets/images/logo.png';
import { HashLink } from 'react-router-hash-link';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const isAgentScientist = location.pathname === '/agent-scientist';
    const isEvents = location.pathname.startsWith('/events') || location.pathname === '/scisprint-2026';

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileOpen]);

    const closeMobile = () => setMobileOpen(false);

    return (
        <>
            {mobileOpen && (
                <div 
                    className="cb-mobile-backdrop" 
                    onClick={closeMobile}
                    aria-hidden="true" 
                />
            )}
            <header className={`cb-header-wrapper ${scrolled ? 'is-scrolled' : ''} ${mobileOpen ? 'is-open' : ''}`}>
                <nav className={`cb-header ${scrolled ? 'scrolled' : ''}`}>
                <div className="cb-nav-container">
                    <ul className="cb-nav-menu cb-nav-left">
                        <li>
                            <HashLink 
                                className={`cb-nav-link ${location.pathname === '/' && !location.hash.includes('learn') ? 'active' : ''}`} 
                                to="/#home"
                                onClick={closeMobile}
                            >
                                <span>Home</span>
                            </HashLink>
                        </li>
                        <li>
                            <Link 
                                className={`cb-nav-link cb-nav-agent ${isAgentScientist ? 'active' : ''}`} 
                                to="/agent-scientist"
                                onClick={closeMobile}
                            >
                                <span>Agent Scientist</span>
                            </Link>
                        </li>
                    </ul>

                    <HashLink className="cb-brand" to="/#home" onClick={closeMobile} aria-label="ChemBioAI Home">
                        <img className="cb-brand-logo" src={logo} alt="ChemBioAI" />
                    </HashLink>

                    <div className="cb-nav-right">
                        <ul className="cb-nav-menu cb-nav-menu-right">
                            <li>
                                <Link 
                                    className={`cb-nav-link ${isEvents ? 'active' : ''}`} 
                                    to="/events"
                                    onClick={closeMobile}
                                >
                                    <span>Events</span>
                                </Link>
                            </li>
                            <li>
                                <Link 
                                    className={`cb-nav-link ${location.pathname.startsWith('/learn') ? 'active' : ''}`} 
                                    to="/learn"
                                    onClick={closeMobile}
                                >
                                    <span>Learn</span>
                                </Link>
                            </li>
                        </ul>

                        <div className="cb-social-links">
                            <a 
                                className="cb-social-link" 
                                href="https://x.com/chembio_ai" 
                                target="_blank" 
                                rel="noreferrer"
                                aria-label="X (Twitter)"
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                            </a>
                            <a 
                                className="cb-social-link" 
                                href="https://www.linkedin.com/company/chembio-ai/" 
                                target="_blank" 
                                rel="noreferrer"
                                aria-label="LinkedIn"
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                                </svg>
                            </a>
                        </div>

                        <a 
                            className="cb-header-cta" 
                            href="https://discord.gg/nGtFAR3hwn" 
                            target="_blank" 
                            rel="noreferrer"
                        >
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.929 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.894.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                            </svg>
                            <span>Join Discord</span>
                        </a>
                    </div>

                    <button 
                        className="cb-mobile-toggle" 
                        onClick={() => setMobileOpen(!mobileOpen)}
                        aria-label="Toggle Navigation Menu"
                    >
                        {mobileOpen ? (
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="4" y1="8" x2="20" y2="8"></line>
                                <line x1="4" y1="16" x2="20" y2="16"></line>
                            </svg>
                        )}
                    </button>
                </div>

                <div className={`cb-mobile-drawer ${mobileOpen ? 'open' : ''}`}>
                    <ul className="cb-mobile-menu">
                        <li>
                            <HashLink className="cb-mobile-link" to="/#home" onClick={closeMobile}>Home</HashLink>
                        </li>
                        <li>
                            <Link className="cb-mobile-link" to="/agent-scientist" onClick={closeMobile}>
                                Agent Scientist
                            </Link>
                        </li>
                        <li>
                            <Link className="cb-mobile-link" to="/events" onClick={closeMobile}>
                                Events
                            </Link>
                        </li>
                        <li>
                            <Link className="cb-mobile-link" to="/learn" onClick={closeMobile}>Learn</Link>
                        </li>
                    </ul>

                    <div className="cb-mobile-actions">
                        <div className="cb-social-links">
                            <a 
                                className="cb-social-link" 
                                href="https://x.com/chembio_ai" 
                                target="_blank" 
                                rel="noreferrer"
                                aria-label="X (Twitter)"
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                            </a>
                            <a 
                                className="cb-social-link" 
                                href="https://www.linkedin.com/company/chembio-ai/" 
                                target="_blank" 
                                rel="noreferrer"
                                aria-label="LinkedIn"
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                                </svg>
                            </a>
                        </div>

                        <a 
                            className="cb-header-cta" 
                            href="https://discord.gg/nGtFAR3hwn" 
                            target="_blank" 
                            rel="noreferrer"
                            onClick={closeMobile}
                        >
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.929 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.894.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                            </svg>
                            <span>Join Discord</span>
                        </a>
                    </div>
                </div>
            </nav>
        </header>
        </>
    );
};

export default Header;
